import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import "../assets/css/GuesthouseBooking.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const BOOKING_ALLOWED_EXTS   = ["jpg", "jpeg", "png"];
const BOOKING_MAX_FILE_BYTES = 200 * 1024;

const compressBookingImage = (file, maxBytes) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width  = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      let quality = 0.92;
      const attempt = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Compression failed."));
            if (blob.size <= maxBytes || quality < 0.1) return resolve(blob);
            quality = Math.max(quality - 0.08, 0.05);
            attempt();
          },
          "image/jpeg",
          quality
        );
      };
      attempt();
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Cannot read image.")); };
    img.src = url;
  });

function toTitleCase(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trimStart()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseLocalDate(value) {
  if (!value) return null;
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatDateInput(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function GuesthouseBooking() {

  // Correct initialization order
  const { user } = useAuth();
  const localUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const currentUser = user || localUser;
  const moduleId = parseInt(currentUser?.moduleId, 10);
  const employeeID = currentUser?.id;

  // Restore all required state and variables
  const [formType, setFormType] = useState("G");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [guesthouses, setGuesthouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    visitors: [
      { name: "", mobile: "", email: "", department: "", relationship: "" }
    ],
    address: "",
    stateID: "",
    cityID: "",
    guestHouseID: "",
    visitType: "",
    purposeOfVisit: "Conference",
    noOfRooms: 1,
    noOfDays: 1,
    fromDate: "",
    toDate: "",
    roomType: "R",
    relationWithApplicant: "",
    remarks: "",
    supportDoc: null
  });

  // Fetch states on component mount
  const fetchStates = async () => {
    try {
      const res = await fetch(`${API_ROOT}/api/states`);
      const data = await res.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      setStates([]);
      console.error("Failed to fetch states:", err);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  // Fetch cities when stateID changes
  useEffect(() => {
    if (!formData.stateID) {
      setCities([]);
      return;
    }
    const fetchCities = async () => {
      try {
        const res = await fetch(`${API_ROOT}/api/cities?stateId=${formData.stateID}`);
        const data = await res.json();
        setCities(Array.isArray(data) ? data : []);
      } catch (err) {
        setCities([]);
        console.error("Failed to fetch cities:", err);
      }
    };
    fetchCities();
  }, [formData.stateID]);

  // Handle dynamic visitor changes
  const handleVisitorChange = (idx, field, value) => {
    setFormData(prev => {
      const updated = [...prev.visitors];
      updated[idx][field] = value;
      return { ...prev, visitors: updated };
    });
  };


  // Sync visitors array with noOfRooms, module-specific max
  useEffect(() => {
    const maxRooms = [1, 13, 17, 19, 21].includes(moduleId) ? 2 : moduleId === 15 ? 1 : 10;
    setFormData(prev => {
      let visitors = prev.visitors;
      const rooms = Math.max(1, Math.min(prev.noOfRooms, maxRooms));
      if (visitors.length < rooms) {
        visitors = [
          ...visitors,
          ...Array(rooms - visitors.length).fill().map(() => ({ name: "", mobile: "", relationship: "" }))
        ];
      } else if (visitors.length > rooms) {
        visitors = visitors.slice(0, rooms);
      }
      return { ...prev, visitors };
    });
  }, [formData.noOfRooms, moduleId]);

  // Standard input change handler with module-specific room limit
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (name === "noOfRooms") {
      const maxRooms = [1, 13, 17, 19, 21].includes(moduleId) ? 2 : moduleId === 15 ? 1 : 10;
      let val = parseInt(value, 10);
      if (isNaN(val) || val < 1) val = 1;
      if (val > maxRooms) val = maxRooms;
      setFormData(prev => ({ ...prev, noOfRooms: val }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === "number" ? parseInt(value, 10) : value }));
    }
  };

  // Fetch guest houses on component mount
  useEffect(() => {
    const fetchGuesthouses = async () => {
      try {
        const res = await fetch(
          `${API_ROOT}/api/guesthouse/booking/guesthouses?moduleId=${moduleId}`
        );
        const data = await res.json();
        setGuesthouses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch guest houses:", err);
      }
    };
    fetchGuesthouses();
  }, [moduleId]);

  // Fetch employee details when form type is "Self"
  const handleFormTypeChange = async (type) => {
    setFormType(type);

    if (type === "G") {
      // Clear all fields for Guest
      setFormData(prev => ({
        ...prev,
        visitors: [
          {
            ...(prev.visitors[0] || {}),
            name: "",
            mobile: "",
            email: "",
            department: "",
            relationship: ""
          },
          ...prev.visitors.slice(1)
        ],
        address: "",
        stateID: "",
        cityID: "",
        relationWithApplicant: "",
        visitType: ""
      }));
      return;
    }

    if (type !== "S") return;

    // Fetch employee details for Self
    try {
      setLoading(true);
      const res = await fetch(
        `${API_ROOT}/api/profile/employee/${employeeID}?moduleId=${moduleId}`
      );
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Failed to fetch employee details");
        return;
      }

      setFormData(prev => ({
        ...prev,
        visitors: [
          {
            ...(prev.visitors[0] || {}),
            name: data.name || "",
            mobile: data.mobile || "",
            email: data.email || "",
            department: data.department || "",
            relationship: "Self"
          },
          ...prev.visitors.slice(1)
        ],
        address: data.permanentAddress || "",
        stateID: data.permanentStateID || "",
        cityID: data.permanentCityID || "",
        relationWithApplicant: "Self",
        visitType: moduleId === 17 ? "" : "P"
      }));
    } catch (err) {
      setError("Error fetching employee details");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Update room types based on guest house
  const handleGuestHouseChange = (e) => {
    const ghID = parseInt(e.target.value, 10);
    setFormData(prev => ({
      ...prev,
      guestHouseID: ghID,
      roomType: "R"
    }));
  };

  const handleDateChange = (field, value) => {
    const selectedDate = parseLocalDate(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!selectedDate) {
      setFormData(prev => ({
        ...prev,
        [field]: "",
        noOfDays: 1
      }));
      setFieldErrors(prev => ({ ...prev, [field]: "" }));
      return;
    }

    if (field === "fromDate") {
      if (selectedDate < today) {
        setFormData(prev => ({ ...prev, fromDate: "", noOfDays: 1 }));
        setFieldErrors(prev => ({ ...prev, fromDate: "Date must be today or later" }));
        return;
      }

      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + 30);
      if (selectedDate > maxDate) {
        setFormData(prev => ({ ...prev, fromDate: "", noOfDays: 1 }));
        setFieldErrors(prev => ({ ...prev, fromDate: "Date must be within 30 days from the current date" }));
        return;
      }
    }

    setFormData(prev => {
      const nextData = {
        ...prev,
        [field]: value
      };

      if (nextData.fromDate && nextData.toDate) {
        const from = parseLocalDate(nextData.fromDate);
        const to = parseLocalDate(nextData.toDate);

        if (!from || !to) {
          nextData.noOfDays = 1;
          return nextData;
        }

        if (to < from) {
          nextData.toDate = "";
          nextData.noOfDays = 1;
          setFieldErrors(prevErrors => ({ ...prevErrors, toDate: "Check-out date must be after check-in date" }));
          return nextData;
        }

        const diffTime = to.getTime() - from.getTime();
        const daysDifference = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (daysDifference > 3) {
          nextData.toDate = "";
          nextData.noOfDays = 1;
          setFieldErrors(prevErrors => ({ ...prevErrors, noOfDays: "Maximum Days allowed is 3" }));
          return nextData;
        }

        nextData.noOfDays = daysDifference;
        setFieldErrors(prevErrors => ({ ...prevErrors, toDate: "", noOfDays: "", fromDate: "" }));
      } else {
        nextData.noOfDays = 1;
      }

      return nextData;
    });
  };


  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "visitorMobile") {
      if (value && value.length !== 10) {
        setFieldErrors(prev => ({ ...prev, visitorMobile: "10 digits only" }));
      } else {
        setFieldErrors(prev => ({ ...prev, visitorMobile: "" }));
      }
    } else if (name === "visitorEmail") {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setFieldErrors(prev => ({ ...prev, visitorEmail: "Invalid email address" }));
      } else {
        setFieldErrors(prev => ({ ...prev, visitorEmail: "" }));
      }
    }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) { setFormData(prev => ({ ...prev, supportDoc: null })); return; }

    const ext = f.name.split(".").pop().toLowerCase();
    if (!BOOKING_ALLOWED_EXTS.includes(ext)) {
      setError("Only JPG, JPEG, and PNG files are allowed for the support document.");
      e.target.value = "";
      setFormData(prev => ({ ...prev, supportDoc: null }));
      return;
    }

    setError("");

    if (f.size <= BOOKING_MAX_FILE_BYTES) {
      setFormData(prev => ({ ...prev, supportDoc: f }));
      return;
    }

    try {
      const blob = await compressBookingImage(f, BOOKING_MAX_FILE_BYTES);
      const compressed = new File([blob], f.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
      setFormData(prev => ({ ...prev, supportDoc: compressed }));
    } catch {
      setError("Image is too large and could not be compressed. Please use a smaller file.");
      e.target.value = "";
      setFormData(prev => ({ ...prev, supportDoc: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const nextErrors = {};

    const firstVisitor = formData.visitors[0] || {};

    if (!firstVisitor.name?.trim()) nextErrors.visitorName = "Visitor Name is required";

    if (!firstVisitor.mobile) {
      nextErrors.visitorMobile = "Visitor Mobile is required";
    } else if (!/^\d{10}$/.test(firstVisitor.mobile)) {
      nextErrors.visitorMobile = "Mobile must be exactly 10 digits";
    }

    if (firstVisitor.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(firstVisitor.email)) {
      nextErrors.visitorEmail = "Invalid email address";
    }

    if (!formData.address?.trim()) nextErrors.address = "Visitor's Address is required";
    if (!formData.stateID) nextErrors.stateID = "State is required";
    if (!formData.cityID) nextErrors.cityID = "City is required";
    if (!formData.guestHouseID) nextErrors.guestHouseID = "Please select a Guest House";
    if (!formData.fromDate) nextErrors.fromDate = "Check-In Date is required";
    if (!formData.toDate) nextErrors.toDate = "Check-Out Date is required";
    if (!formData.purposeOfVisit) nextErrors.purposeOfVisit = "Purpose of Visit is required";

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setFieldErrors({});

    // Ensure visitType is set for moduleId 1 and 13
    let visitType = formData.visitType;
    if ([1, 13].includes(moduleId)) {
      visitType = "P";
    }

    // Validate room limit based on module
    const noOfRoomsNum = parseInt(formData.noOfRooms, 10) || 1;
    const maxRoomsAllowed = [1, 13, 17, 19, 21].includes(moduleId) ? 2 : moduleId === 15 ? 1 : 10;
    if (noOfRoomsNum > maxRoomsAllowed) {
      setError(`Maximum ${maxRoomsAllowed} room(s) allowed for booking`);
      return;
    }

    try {
      setLoading(true);

      // Prepare all required fields for booking_request
      const submitData = {
        ...formData,
        purposeOfVisit: formData.purposeOfVisit || "Personal",
        visitType,
        visitorName: toTitleCase(firstVisitor.name),
        visitorMobile: firstVisitor.mobile,
        visitorEmail: firstVisitor.email || "",
        department: firstVisitor.department || "",
        relationWithApplicant: firstVisitor.relationship || "Self",
        moduleId,
        employeeID: [1, 2, 5, 13, 15, 19].includes(moduleId) ? employeeID : null,
        ghMemberID: moduleId === 17 ? employeeID : null,
        departmentID: [2, 5, 15].includes(moduleId) ? currentUser?.deptid : null,
        teacherID: moduleId === 21 ? employeeID : null,
        paymentMode: "Cash", // default value as in PHP
        noOfRooms: formData.noOfRooms || 1,
        noOfDays: formData.noOfDays || 1,
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        roomType: formData.roomType || "R"
      };

      // Remove visitors array from main payload
      delete submitData.visitors;

      const payload = new FormData();
      Object.entries(submitData).forEach(([key, value]) => {
        if (key === "supportDoc") {
          if (value) {
            payload.append("supportDoc", value);
          }
          return;
        }
        if (value !== null && value !== undefined && value !== "") {
          payload.append(key, String(value));
        }
      });

      if (formData.visitors.length > 1) {
        // Prepare array with default values for each visitor
        const additionalVisitors = formData.visitors.slice(1).map((v, idx) => ({
          name: toTitleCase(v.name),
          mobile: v.mobile,
          relationship: v.relationship || "Guest",
          paymentMode: "Cash",
          fromDate: formData.fromDate,
          toDate: formData.toDate,
          noOfRooms: 1,
          noOfDays: formData.noOfDays || 1,
          employeeID: submitData.employeeID,
          ghMemberID: submitData.ghMemberID,
          departmentID: submitData.departmentID,
          teacherID: submitData.teacherID
        }));
        payload.append("visitors", JSON.stringify(additionalVisitors));
      }

      const res = await fetch(`${API_ROOT}/api/guesthouse/booking/create`, {
        method: "POST",
        body: payload
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Failed to create booking");
        return;
      }

      const refs = data.bookingRefs && data.bookingRefs.length > 1
        ? ` Booking References: ${data.bookingRefs.join(", ")}.`
        : ` Booking ID: ${data.bookingID}.`;
      setSuccess((data.message || "Booking request submitted successfully.") + refs);

      // Reset form
      setFormData({
        visitors: [
          { name: "", mobile: "", email: "", department: "", relationship: "" }
        ],
        address: "",
        stateID: "",
        cityID: "",
        guestHouseID: "",
        visitType: "",
        purposeOfVisit: "Conference",
        noOfRooms: 1,
        noOfDays: 1,
        fromDate: "",
        toDate: "",
        roomType: "R",
        relationWithApplicant: "",
        remarks: "",
        supportDoc: null
      });

      // Move to in-app booking status page after successful request.
      setTimeout(() => {
        window.location.href = "/guesthouse/booking-status";
      }, 1500);
    } catch (err) {
      setError("Error submitting booking request");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRoomTypeOptions = () => {
    const baseOption = [{ value: "R", label: "Room" }];
    
    if (formData.guestHouseID === 1) {
      baseOption.push({ value: "S", label: "Suite" });
    } else if (formData.guestHouseID === 6) {
      baseOption.push({ value: "H", label: "Seminar Hall" });
    }
    
    return baseOption;
  };

  const getPurposeOfVisitOptions = () => {
    const common = ["Conference", "Seminar", "Workshop", "Symposia", "Selection Committee", "Lecture", "Official Meeting", "Evaluation Duty"];
    
    if ([2, 5, 15].includes(moduleId)) {
      return [...common, "Viva/External Examiner", "Others"];
    } else {
      return [...common, "Personal", "Others"];
    }
  };

  const getFormTypeLabel = () => {
    if ([2, 5, 15].includes(moduleId)) {
      return { G: "Official Guest", S: "Self" };
    }
    return { G: "Guest", S: "Self" };
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minCheckInDate = new Date(today);
  const maxCheckInDate = new Date(today);
  maxCheckInDate.setDate(maxCheckInDate.getDate() + 30);

  const minCheckOutDate = formData.fromDate
    ? parseLocalDate(formData.fromDate)
    : minCheckInDate;
  const maxCheckOutDate = formData.fromDate
    ? (() => {
        const d = parseLocalDate(formData.fromDate);
        if (!d) return maxCheckInDate;
        const cap = new Date(d);
        cap.setDate(cap.getDate() + 30);
        return cap;
      })()
    : maxCheckInDate;

  if (error && error.includes("Unauthorized")) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left", marginTop: "10px" }}>
      {moduleId !== 10 && (
        <a
          href="/guesthouse/booking-status"
          className="btn btn-primary"
          style={{ display: "inline-block", padding: "10px 20px", textDecoration: "none" }}
        >
          Booking Status
        </a>
      )}


      <h3 className="ctitle">Booking Request (Visitor's Detail)</h3>

      <div className="col-lg-10">
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form
          onSubmit={handleSubmit}
          className="guesthouse-booking-form"
          name="BookingRequestForm"
          id="BookingRequestForm"
          encType="multipart/form-data"
        >
          {/* UNDO POINT 2026-05-29: visitor-details redesign starts below */}
          <div className="booking-top-controls">
            <div className="booking-control-item" id="FormType">
              <label className="form-label">Type</label>
              <select
                className="form-select form-select-sm"
                name="FormType"
                id="FormTypeSelect"
                value={formType}
                onChange={(e) => handleFormTypeChange(e.target.value)}
              >
                {Object.entries(getFormTypeLabel()).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Purpose of Visit</label>
              <select
                className="form-select form-select-sm"
                name="purposeOfVisit"
                id="PurposeOfVisit"
                value={formData.purposeOfVisit}
                onChange={handleInputChange}
                required
              >
                {getPurposeOfVisitOptions().map((label, idx) => (
                  <option key={idx} value={label}>{label}</option>
                ))}
              </select>
            </div>

            <div className="booking-control-item booking-control-guesthouse">
              <label className="form-label">Select Guest House</label>
              <select
                className="form-select form-select-sm"
                name="guestHouseID"
                id="GuestHouseID"
                value={formData.guestHouseID}
                onChange={handleGuestHouseChange}
                required
              >
                <option value="">Select Guest House</option>
                {guesthouses.map(gh => (
                  <option key={gh.GuestHouseID} value={gh.GuestHouseID}>
                    {gh.GuestHouse}
                  </option>
                ))}
              </select>
              {fieldErrors.guestHouseID && <div className="field-error">{fieldErrors.guestHouseID}</div>}
            </div>

            <div className="booking-control-item booking-control-roomtype" id="roomTypeContainer">
              <label className="form-label">Room Type</label>
              <select
                className="form-select form-select-sm"
                name="roomType"
                id="RoomType"
                value={formData.roomType}
                onChange={handleInputChange}
              >
                {getRoomTypeOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Check-In Date</label>
              <input
                className="form-control form-control-sm"
                name="fromDate"
                type="date"
                id="FromDate"
                value={formData.fromDate}
                onChange={(e) => handleDateChange("fromDate", e.target.value)}
                min={formatDateInput(minCheckInDate)}
                max={formatDateInput(maxCheckInDate)}
                required
              />
              {fieldErrors.fromDate && <div className="field-error">{fieldErrors.fromDate}</div>}
            </div>

            <div className="booking-control-item">
              <label className="form-label">Check-Out Date</label>
              <input
                className="form-control form-control-sm"
                name="toDate"
                type="date"
                id="ToDate"
                value={formData.toDate}
                onChange={(e) => handleDateChange("toDate", e.target.value)}
                min={formatDateInput(minCheckOutDate)}
                max={formatDateInput(maxCheckOutDate)}
                required
              />
              {fieldErrors.toDate && <div className="field-error">{fieldErrors.toDate}</div>}
            </div>

            <div className="booking-control-item booking-control-days">
              <label className="form-label">No. Of Days</label>
              <input
                className="form-control form-control-sm"
                name="noOfDays"
                type="number"
                id="NoOfDays"
                value={formData.noOfDays}
                onChange={handleInputChange}
                min="1"
                max="3"
                readOnly
              />
              {fieldErrors.noOfDays && <div className="field-error">{fieldErrors.noOfDays}</div>}
            </div>

            <div className="booking-control-item booking-control-rooms">
              <label className="form-label">No. Of Rooms</label>
              <input
                className="form-control form-control-sm"
                type="number"
                name="noOfRooms"
                id="NoOfRooms"
                value={formData.noOfRooms}
                onChange={handleInputChange}
                min="1"
                max={[1, 13, 17, 19, 21].includes(moduleId) ? 2 : moduleId === 15 ? 1 : 10}
                required
              />
              <small className="booking-hint-text">
                {[1, 13, 17, 19, 21].includes(moduleId) ? 'Max 2 rooms' : moduleId === 15 ? 'Max 1 room' : 'Max 10 rooms allowed'}
              </small>
            </div>
          </div>

          {formData.visitors.length > 0 && (
            <>
            <div className="visitor-details-heading">Visitor Details</div>
            <div className="visitor-details-card">
              {formData.visitors.map((visitor, idx) => {
                const roomNum = idx + 1;
                const getOrdinal = (n) => {
                  if (n === 1) return '1st';
                  if (n === 2) return '2nd';
                  if (n === 3) return '3rd';
                  const v = n % 100;
                  if (v >= 11 && v <= 13) return `${n}th`;
                  switch (n % 10) {
                    case 1: return `${n}st`;
                    case 2: return `${n}nd`;
                    case 3: return `${n}rd`;
                    default: return `${n}th`;
                  }
                };

                return (
                  <div className="visitor-details-entry" key={idx}>
                    {idx === 0 ? (
                      <div className="visitor-primary-header-inline">
                        <span>Primary Visitor Visitor Name</span>
                        <span>Mobile</span>
                        <span>Relationship</span>
                      </div>
                    ) : (
                      <div className="visitor-entry-title">
                        {`${getOrdinal(roomNum)} Room Visitor`}
                      </div>
                    )}
                    <div className="visitor-details-grid">
                      <div className="visitor-field-item">
                        {idx !== 0 && <label className="form-label">Visitor Name</label>}
                        <input
                          className="form-control form-control-sm"
                          type="text"
                          placeholder="Visitor Name*"
                          value={visitor.name}
                          onChange={e => {
                            const val = e.target.value.replace(/[^A-Za-z0-9 ]/g, "");
                            handleVisitorChange(idx, "name", val);
                          }}
                          required
                        />
                        {idx === 0 && fieldErrors.visitorName && <div className="field-error">{fieldErrors.visitorName}</div>}
                      </div>
                      <div className="visitor-field-item">
                        {idx !== 0 && <label className="form-label">Mobile</label>}
                        <input
                          className="form-control form-control-sm"
                          type="text"
                          placeholder="Mobile*"
                          value={visitor.mobile}
                          onChange={e => handleVisitorChange(idx, "mobile", e.target.value.replace(/[^0-9]/g, ""))}
                          maxLength={10}
                          pattern="\d{10}"
                          required
                        />
                        {idx === 0 && fieldErrors.visitorMobile && <div className="field-error">{fieldErrors.visitorMobile}</div>}
                      </div>
                      <div className="visitor-field-item">
                        {idx !== 0 && <label className="form-label">Relationship</label>}
                        <input
                          className="form-control form-control-sm"
                          type="text"
                          placeholder="Relationship*"
                          value={visitor.relationship || ''}
                          onChange={e => handleVisitorChange(idx, "relationship", e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}



          {/* Visitor Details Row: Address, State, City */}
          <div className="visitor-location-row">
            <div className="visitor-location-item visitor-location-address">
              <label className="form-label">Visitor's Address</label>
              <input
                className="form-control form-control-sm"
                name="address"
                type="text"
                id="Address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Permanent Address..."
                required
              />
              {fieldErrors.address && <div className="field-error">{fieldErrors.address}</div>}
            </div>
            <div className="visitor-location-item visitor-location-state">
              <label className="form-label">Visitor's State</label>
              <select
                className="form-select form-select-sm"
                name="stateID"
                id="StateID"
                value={formData.stateID}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, stateID: e.target.value, cityID: "" }));
                }}
                required
              >
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state.StateID} value={state.StateID}>
                    {state.State}
                  </option>
                ))}
              </select>
              {fieldErrors.stateID && <div className="field-error">{fieldErrors.stateID}</div>}
            </div>
            <div className="visitor-location-item visitor-location-city">
              <label className="form-label">Visitor City</label>
              <select
                className="form-select form-select-sm"
                name="cityID"
                id="CityID"
                value={formData.cityID}
                onChange={handleInputChange}
                required
              >
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city.CityID} value={city.CityID}>
                    {city.City}
                  </option>
                ))}
              </select>
              {fieldErrors.cityID && <div className="field-error">{fieldErrors.cityID}</div>}
            </div>

          </div>

          <div className="visitor-extra-row">
            <div className="visitor-extra-item">
              <label className="form-label">Visitor Department (Optional)</label>
              <input
                className="form-control form-control-sm"
                name="department"
                type="text"
                id="Department"
                value={formData.visitors[0]?.department || ""}
                onChange={(e) => handleVisitorChange(0, "department", e.target.value)}
                placeholder="Department..."
              />
            </div>

            <div className="visitor-extra-item">
              <label className="form-label">Visitor's EMail</label>
              <input
                className="form-control form-control-sm"
                name="visitorEmail"
                type="email"
                id="VisitorEMail"
                value={formData.visitors[0]?.email || ""}
                onChange={(e) => handleVisitorChange(0, "email", e.target.value)}
                onBlur={handleBlur}
                placeholder="EMail..."
              />
              {fieldErrors.visitorEmail && <div className="field-error">{fieldErrors.visitorEmail}</div>}
            </div>

            <div className="visitor-extra-item visitor-extra-remarks">
              <label className="form-label">Remarks</label>
              <input
                className="form-control form-control-sm"
                name="remarks"
                type="text"
                id="Remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                placeholder="Remarks..."
              />
            </div>

            <div className="visitor-extra-item visitor-extra-upload">
              <label className="form-label">
                Any letter of invitation/sponsorship&nbsp;
                <i
                  className="bi bi-info-circle text-primary"
                  title="Allowed: JPG, JPEG, PNG only. Max size 200 KB. Larger images will be auto-compressed."
                  style={{ cursor: "help" }}
                ></i>
              </label>
              <input
                type="file"
                name="supportDoc"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png"
                aria-label="Upload invitation or sponsorship document. Accepted formats: JPG, JPEG, PNG, up to 200 KB (auto-compressed if larger)."
              />
              <small className="text-muted">JPG/JPEG/PNG only, max 200KB (auto-compress)</small>
            </div>

          </div>

          {/* Employee ID hidden from UI */}

          {/* Submit Button */}
          <div className="col-sm-12">
            <button
              type="submit"
              name="add"
              id="add"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>

          {/* Instructions */}
          <div className="col-sm-12">
            <p>
              Save this request form, get a printout of the same from Booking Status and submit
              hard copy in the guest house after getting it verified from your Head.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
